import { useState, useEffect, useCallback, useRef } from 'react';
import SummaryStrip from '../components/orders/SummaryStrip';
import OrdersTable from '../components/orders/OrdersTable';
import InvitationsList from '../components/orders/InvitationsList';
import HealthHub from '../components/orders/HealthHub';
import QualityCheck from '../components/orders/QualityCheck';
import ImapSettings from '../components/orders/ImapSettings';
import AccountsList from '../components/orders/AccountsList';
import OrderTracking from '../components/orders/OrderTracking';
import { useMobile } from '../hooks/useMobile';

const TABS = ['Accounts', 'Orders', 'Misc Orders', 'Invitations', 'Quality Check', 'Health'];

function agoLabel(ts) {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
}

const TAB_ICONS = {
  'Accounts':    '👥',
  'Orders':      '📦',
  'Misc Orders': '🚫',
  'Invitations': '🎫',
  'Quality Check': '🔍',
  'Health':      '🩺',
};

export default function OrderDashboard() {
  const isMobile = useMobile();

  const [dashboard, setDashboard]               = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [tab, setTab]                           = useState('Accounts');
  const [accountStatusFilter, setAccountStatusFilter] = useState('active');
  const [tabData, setTabData]                   = useState([]);
  const [syncing, setSyncing]                   = useState(false);
  const [syncResult, setSyncResult]             = useState(null);
  const [backfilling, setBackfilling]           = useState(false);
  const [backfillResult, setBackfillResult]     = useState(null);
  const [tracing, setTracing]                   = useState(null);
  const [traceResult, setTraceResult]           = useState(null);
  // How far back a manual trace reaches. The hourly job only covers 2 days, so
  // this is how a longer stretch gets pulled in one go — after a spell with the
  // sync off, say. Capped at the store's 45-day retention, since anything older
  // is pruned on write and would be scraped only to be discarded.
  const [traceDays, setTraceDays]               = useState(7);
  const [error, setError]                       = useState(null);
  const [statusFilter, setStatusFilter]         = useState('active');
  const [showSettings, setShowSettings]         = useState(false);
  const [healthWindow, setHealthWindow]         = useState('all');
  const [lastFetched, setLastFetched]           = useState(null);
  const [refreshing, setRefreshing]             = useState(false);
  const [, forceTick]                           = useState(0);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch('/api/dashboard');
      if (!res.ok) throw new Error('Backend unreachable');
      const data = await res.json();
      setDashboard(data);
      if (!selectedProvider && data.providers.length) setSelectedProvider(data.providers[0].id);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [selectedProvider]);

  const fetchTab = useCallback(async () => {
    if (!selectedProvider) return;
    // Quality Check loads its own data from the invite-log store, and Misc
    // Orders its own analysis — neither fits the one-endpoint-per-tab shape.
    if (tab === 'Quality Check' || tab === 'Misc Orders') return;
    const endpoint =
      tab === 'Accounts'    ? `/api/accounts?provider=${selectedProvider}${statusFilter ? `&status=${statusFilter}` : ''}` :
      tab === 'Orders'      ? `/api/orders?provider=${selectedProvider}${statusFilter ? `&status=${statusFilter}` : ''}` :
      tab === 'Health'      ? `/api/health-analysis?window=${healthWindow}` :
                              `/api/invitations?provider=${selectedProvider}`;
    try {
      const res = await fetch(endpoint);
      setTabData(await res.json());
    } catch { setTabData([]); }
  }, [selectedProvider, tab, statusFilter, healthWindow]);

  useEffect(() => {
    fetch('/api/settings').then(r => r.json()).then(d => {
      if (!d.configured) setShowSettings(true);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchTab(); }, [fetchTab]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchDashboard(), fetchTab()]);
    setLastFetched(Date.now());
    setRefreshing(false);
  }, [fetchDashboard, fetchTab]);

  // Auto-refresh. The data behind this page moves on its own — the sync runs
  // hourly and the app writes as invites are logged — but the fetches above
  // only fire on mount and when a tab or filter changes, so a page left open
  // showed numbers frozen at whenever it was opened.
  //
  // Held in a ref so the interval is installed once instead of being torn down
  // and restarted every time a filter changes the fetch callbacks' identity.
  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  useEffect(() => {
    let last = Date.now();
    const run = (minGapMs) => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - last < minGapMs) return;   // focus + visibilitychange both fire
      last = Date.now();
      refreshRef.current();
    };
    const onInterval = () => run(0);
    const onWake     = () => run(5000);

    // Re-render periodically so the "updated Xs ago" label counts up rather
    // than freezing at whatever it said when the last fetch landed.
    const tickId = setInterval(() => forceTick(n => n + 1), 15000);
    const id = setInterval(onInterval, 60000);
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      clearInterval(id);
      clearInterval(tickId);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);

  async function handleSync() {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch('/api/sync', { method: 'POST' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      setSyncResult(data.results);
      await fetchDashboard();
      await fetchTab();
    } catch (e) {
      setError(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  }

  async function handleBackfill() {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await fetch('/api/backfill-orders', { method: 'POST' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      setBackfillResult(data.results);
      await fetchTab();
    } catch (e) {
      setError(`Backfill failed: ${e.message}`);
    } finally {
      setBackfilling(false);
    }
  }

  // Finds the last week's cancellation emails, then reads back through each
  // affected recipient's own Amazon mail to recover when those orders were
  // placed. The normal sync cannot do this: it reads one inbox, 25 emails at a
  // time, from the last sync onwards, so a confirmation that predates it is
  // never seen — and without it a cancellation can be counted but never timed.
  async function handleTrace(days = traceDays) {
    setTracing(true);
    setTraceResult(null);
    try {
      const res = await fetch('/api/trace-cancellations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      setTraceResult(data.results);
      await fetchDashboard();
    } catch (e) {
      setError(`Cancellation trace failed: ${e.message}`);
    } finally {
      setTracing(false);
    }
  }

  const providerInfo = dashboard?.providers?.find(p => p.id === selectedProvider);

  return (
    <div style={{ maxWidth: 1200 }}>
      {/* ── Page header ─────────────────────────────── */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 28,
      }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, letterSpacing: '-0.5px', color: '#f1f5f9' }}>
            Order Dashboard
          </h1>
          <p style={{ fontSize: 12, color: '#475569', marginTop: 3 }}>
            {dashboard?.lastSync
              ? `Last synced ${new Date(dashboard.lastSync).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
              : 'Not yet synced'}
            {lastFetched && (
              <span style={{ color: '#334155' }}>
                {' · '}{refreshing ? 'refreshing…' : `page updated ${agoLabel(lastFetched)}`}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setShowSettings(s => !s)}
            title="IMAP Settings"
            style={{
              background: showSettings ? '#334155' : '#1e293b',
              color: showSettings ? '#e2e8f0' : '#64748b',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 10, padding: '9px 13px', fontSize: 16,
              cursor: 'pointer', lineHeight: 1, transition: 'all 0.15s',
            }}
          >⚙</button>
          <button
            onClick={refresh}
            disabled={refreshing}
            title="Reload the dashboard data (does not run an inbox sync)"
            style={{
              background: '#1e293b',
              color: refreshing ? '#475569' : '#64748b',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 10, padding: '9px 13px', fontSize: 15,
              cursor: refreshing ? 'wait' : 'pointer', lineHeight: 1, transition: 'all 0.15s',
            }}
          >
            <span style={{ display: 'inline-block', animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }}>↻</span>
          </button>
          {tab === 'Orders' && (
            <button
              onClick={handleBackfill}
              disabled={backfilling || syncing}
              title="Re-scan last 6 months of shipped/delivered/cancelled emails to update order statuses"
              style={{
                background: backfilling ? '#1e293b' : '#0f172a',
                color: backfilling ? '#475569' : '#94a3b8',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10, padding: '9px 14px',
                fontSize: 13, fontWeight: 500,
                cursor: (backfilling || syncing) ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {backfilling
                ? <><span style={{ display: 'inline-block', animation: 'spin 0.8s linear infinite' }}>↻</span> Scanning…</>
                : '↻ Update statuses'}
            </button>
          )}
          {tab === 'Misc Orders' && (
            <select
              value={traceDays}
              onChange={e => setTraceDays(Number(e.target.value))}
              disabled={tracing || syncing}
              title="How far back the trace reaches. The hourly sync covers 2 days; use this to pull a longer stretch in one go."
              style={{
                background: '#0f172a', color: '#94a3b8',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10, padding: '9px 8px', fontSize: 12, fontWeight: 500,
                cursor: (tracing || syncing) ? 'not-allowed' : 'pointer',
              }}
            >
              {[1, 2, 7, 14, 30, 45].map(d => (
                <option key={d} value={d}>{d === 1 ? '24 hours' : `${d} days`}</option>
              ))}
            </select>
          )}
          {tab === 'Misc Orders' && (
            <button
              onClick={() => handleTrace()}
              disabled={tracing || syncing}
              title={`Sweep the last ${traceDays === 1 ? '24 hours' : `${traceDays} days`} of order confirmations and cancellations, and work out how long each order survived`}
              style={{
                background: tracing ? '#1e293b' : '#0f172a',
                color: tracing ? '#475569' : '#94a3b8',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10, padding: '9px 14px',
                fontSize: 13, fontWeight: 500,
                cursor: (tracing || syncing) ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {tracing
                ? <><span style={{ display: 'inline-block', animation: 'spin 0.8s linear infinite' }}>↻</span> Tracing {traceDays === 1 ? '24h' : `${traceDays}d`}…</>
                : '⟲ Trace orders'}
            </button>
          )}
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              flex: isMobile ? 1 : undefined,
              background: syncing ? '#1e293b' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: syncing ? '#475569' : '#fff',
              border: syncing ? '1px solid rgba(255,255,255,0.08)' : 'none',
              borderRadius: 10, padding: '9px 20px',
              fontSize: 13, fontWeight: 600,
              cursor: syncing ? 'not-allowed' : 'pointer',
              boxShadow: syncing ? 'none' : '0 2px 8px rgba(99,102,241,0.4)',
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            }}
          >
            {syncing
              ? <><span style={{ display: 'inline-block', animation: 'spin 0.8s linear infinite' }}>↻</span> Syncing…</>
              : '↻ Sync Inbox'}
          </button>
        </div>
      </div>

      {/* ── Settings panel ──────────────────────────── */}
      {showSettings && (
        <div style={{
          background: '#1e293b', borderRadius: 14,
          border: '1px solid rgba(255,255,255,0.08)',
          padding: isMobile ? '16px' : '22px 26px',
          marginBottom: 16,
          boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
          animation: 'fadeIn 0.2s ease-out',
        }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', marginBottom: 18 }}>IMAP Inboxes</h2>
          <ImapSettings onSaved={() => { setShowSettings(false); fetchDashboard(); }} />
        </div>
      )}

      {/* ── Banners ──────────────────────────────────── */}
      {error && (
        <div style={{
          background: 'rgba(220,38,38,0.15)', color: '#fca5a5',
          borderRadius: 10, padding: '11px 14px', fontSize: 12, marginBottom: 14,
          border: '1px solid rgba(220,38,38,0.3)', fontWeight: 500,
          animation: 'fadeIn 0.2s ease-out',
        }}>
          ⚠ {error === 'Backend unreachable'
            ? 'Backend not running — start with: cd backend && npm start'
            : error}
        </div>
      )}
      {syncResult && (
        <div style={{
          background: 'rgba(16,185,129,0.12)', color: '#6ee7b7',
          borderRadius: 10, padding: '11px 14px', fontSize: 12, marginBottom: 14,
          border: '1px solid rgba(16,185,129,0.25)', fontWeight: 500,
          animation: 'fadeIn 0.2s ease-out',
        }}>
          ✓ {syncResult.orders} orders · {syncResult.accountEvents} events · {syncResult.invitations} invitations
          {syncResult.errors?.length > 0 && (
            <div style={{ color: '#fcd34d', marginTop: 4, fontSize: 11 }}>
              ⚠ {syncResult.errors.map(e => `${e.account}: ${e.error}`).join(', ')}
            </div>
          )}
        </div>
      )}
      {backfillResult && (
        <div style={{
          background: 'rgba(16,185,129,0.12)', color: '#6ee7b7',
          borderRadius: 10, padding: '11px 14px', fontSize: 12, marginBottom: 14,
          border: '1px solid rgba(16,185,129,0.25)', fontWeight: 500,
          animation: 'fadeIn 0.2s ease-out',
        }}>
          ✓ Status backfill: {backfillResult.updated} order{backfillResult.updated !== 1 ? 's' : ''} updated from {backfillResult.checked} emails scanned
        </div>
      )}
      {traceResult && (
        <div style={{
          background: 'rgba(16,185,129,0.12)', color: '#6ee7b7',
          borderRadius: 10, padding: '11px 14px', fontSize: 12, marginBottom: 14,
          border: '1px solid rgba(16,185,129,0.25)', fontWeight: 500,
          animation: 'fadeIn 0.2s ease-out',
        }}>
          ✓ Traced {traceResult.days === 1 ? '24 hours' : `${traceResult.days} days`}:
          {' '}{traceResult.checkouts.toLocaleString()} order{traceResult.checkouts === 1 ? '' : 's'} placed ·
          {' '}{traceResult.cancelledCheckouts.toLocaleString()} cancelled ·
          {' '}{traceResult.standingCheckouts.toLocaleString()} still standing
          {traceResult.checkouts > 0 && (
            <div style={{ marginTop: 4, fontSize: 11, color: '#a7f3d0' }}>
              {((100 * traceResult.cancelledCheckouts) / traceResult.checkouts).toFixed(1)}% cancelled ·
              {' '}{traceResult.cancellationEmails.toLocaleString()} cancellation emails
              {' '}(one per item) · {traceResult.stored.toLocaleString()} orders held in the store
            </div>
          )}
          {traceResult.orphanCancellations > 0 && (
            <div style={{ color: '#fcd34d', marginTop: 4, fontSize: 11 }}>
              ⚠ {traceResult.orphanCancellations.toLocaleString()} cancellation
              {traceResult.orphanCancellations === 1 ? '' : 's'} had no order confirmation in range — those are
              older orders. Trace further back to include them.
            </div>
          )}
          {traceResult.errors?.length > 0 && (
            <div style={{ color: '#fcd34d', marginTop: 4, fontSize: 11 }}>
              ⚠ {traceResult.errors.map(e => `${e.account}${e.recipient ? ` (${e.recipient})` : ''}: ${e.error}`).join(', ')}
            </div>
          )}
        </div>
      )}

      {/* ── Summary strip (KPIs + domain league) ─────── */}
      {dashboard ? (
        <SummaryStrip
          provider={providerInfo}
          health={dashboard.health}
          activeTab={tab}
          onJump={t => {
            setTab(t);
            if (t === 'Accounts') { setStatusFilter('active'); setAccountStatusFilter('active'); }
            else setStatusFilter('');
          }}
          isMobile={isMobile}
        />
      ) : (
        !error && (
          <div style={{ height: isMobile ? 120 : 130, background: '#1e293b', borderRadius: 14, border: '1px solid rgba(255,255,255,0.07)', animation: 'pulse-glow 1.5s ease-in-out infinite', marginBottom: 20 }} />
        )
      )}

      {/* ── Tab panel ────────────────────────────────── */}
      {selectedProvider && (
        <div style={{
          background: '#1e293b',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: isMobile ? 12 : 14,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
        }}>
          {/* Tab bar */}
          <div style={{
            display: 'flex',
            alignItems: 'stretch',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            background: 'rgba(0,0,0,0.2)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}>
            {TABS.map(t => {
              const isActive = tab === t;
              let badge = null;
              if (t === 'Accounts' && providerInfo?.accountCounts?.suspended > 0) {
                badge = (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                    background: 'rgba(220,38,38,0.25)', color: '#f87171',
                  }}>
                    {providerInfo.accountCounts.suspended}
                  </span>
                );
              } else if (t === 'Invitations' && providerInfo?.invitationCount > 0) {
                badge = (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                    background: 'rgba(251,191,36,0.2)', color: '#fcd34d',
                  }}>
                    {providerInfo.invitationCount}
                  </span>
                );
              }

              return (
                <button
                  key={t}
                  onClick={() => {
                    setTab(t);
                    if (t === 'Accounts') { setStatusFilter('active'); setAccountStatusFilter('active'); }
                    else setStatusFilter('');
                  }}
                  style={{
                    background: 'none', border: 'none',
                    borderBottom: `2px solid ${isActive ? '#6366f1' : 'transparent'}`,
                    padding: isMobile ? '12px 10px 10px' : '13px 14px 11px',
                    fontSize: isMobile ? 12 : 13,
                    fontWeight: isActive ? 600 : 450,
                    color: isActive ? '#a5b4fc' : '#475569',
                    cursor: 'pointer', marginBottom: -1,
                    display: 'flex', alignItems: 'center', gap: 5,
                    transition: 'color 0.12s',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: isMobile ? 13 : 14 }}>{TAB_ICONS[t]}</span>
                  {isMobile ? t.split(' ')[0] : t}
                  {badge}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div style={{ padding: isMobile ? '14px' : '20px' }}>
            {tab === 'Accounts' && (
              <AccountsList
                accounts={tabData}
                accountCounts={providerInfo?.accountCounts}
                domainBreakdown={providerInfo?.domainBreakdown || []}
                regionBreakdown={providerInfo?.regionBreakdown || []}
                statusFilter={accountStatusFilter}
                onStatusFilter={s => { setAccountStatusFilter(s); setStatusFilter(s); }}
                onImported={() => { fetchDashboard(); fetchTab(); }}
                selectedProvider={selectedProvider}
                isMobile={isMobile}
              />
            )}
            {tab === 'Orders'      && <OrdersTable orders={tabData} isMobile={isMobile} />}
            {/* Cancellation tracking. Loads its own analysis rather than the
                shared tab fetch: it needs each order's whole email timeline, not
                the current-status list the Orders tab shows. */}
            {tab === 'Misc Orders' && (
              <OrderTracking isMobile={isMobile} tracing={tracing} onTrace={handleTrace} />
            )}
            {/* Reads the invite-log store directly rather than the shared tab
                fetch — it needs every logged outcome, including those whose
                invitation has since aged out of the 72-hour window. */}
            {tab === 'Quality Check' && <QualityCheck isMobile={isMobile} />}
            {tab === 'Invitations' && <InvitationsList invitations={tabData} isMobile={isMobile} onInviteLogChanged={fetchTab} />}
            {tab === 'Health'      && (
              <HealthHub
                analysis={Array.isArray(tabData) ? null : tabData}
                window={healthWindow}
                onWindow={setHealthWindow}
                isMobile={isMobile}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
